import { useEffect, useState, type ChangeEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isApiError, programApi } from "@bw/shared/api";
import {
  useClassAssignments,
  useClassMaterials,
  useClassSubmissions,
  useRemoveClassFile,
  useSaveClassSubmission,
  useSubmitClassSubmission,
  useUploadClassFile,
} from "@bw/shared/hooks";
import type { Schema } from "@bw/shared/types/generated";

function command() {
  return crypto.randomUUID();
}

function statusLabel(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function dueLabel(value: string): string {
  const instant = new Date(value);
  if (!Number.isFinite(instant.getTime())) return "Due date unavailable";
  return `Due ${new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(instant)}`;
}

function learnerError(error: unknown): string {
  if (isApiError(error) && error.status === 409)
    return "This work changed. Reload it before trying again.";
  if (isApiError(error) && [401, 403, 404].includes(error.status))
    return "Your class access changed. Refresh My classes for the latest status.";
  return "We couldn’t complete that Academy action. Your draft is still here.";
}

async function download(fileId: string, enrollmentId: string) {
  const result = await programApi.downloadClassFile(fileId, enrollmentId);
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = result.filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function AcademyLearnerWorkspace({
  programId,
  enrollmentId,
}: {
  programId: string;
  enrollmentId: string;
}) {
  const [materialOffset, setMaterialOffset] = useState(0);
  const [assignmentOffset, setAssignmentOffset] = useState(0);
  const materials = useClassMaterials(programId, enrollmentId, {
    offset: materialOffset,
  });
  const assignments = useClassAssignments(programId, enrollmentId, {
    offset: assignmentOffset,
  });
  const [notice, setNotice] = useState<string | null>(null);
  const accessLost = [materials, assignments].some(
    (query) =>
      query.isError &&
      isApiError(query.error) &&
      [401, 403, 404].includes(query.error.status),
  );
  if (accessLost)
    return (
      <Alert severity="warning">
        Your class access changed. Return to My classes to refresh your current
        enrollments.
      </Alert>
    );
  const loading = materials.isLoading || assignments.isLoading;
  return (
    <Paper component="section" variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: "action.hover" }}>
        <Typography component="h2" variant="h5">
          Academy
        </Typography>
        <Typography color="text.secondary">
          Resources, assignments and private feedback for this enrollment.
        </Typography>
      </Box>
      {loading ? <LinearProgress /> : null}
      <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 } }}>
        {notice ? (
          <Alert severity="warning" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        ) : null}
        {materials.isError || assignments.isError ? (
          <Alert
            severity="error"
            action={
              <Button
                size="small"
                onClick={() => {
                  void materials.refetch();
                  void assignments.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Academy content could not be loaded.
          </Alert>
        ) : null}
        {loading ? (
          <Typography role="status">Loading Academy content…</Typography>
        ) : materials.isError || assignments.isError ? null : (
          <>
            <Box>
              <Typography variant="h6">Course library</Typography>
              <Typography variant="body2" color="text.secondary">
                Protected files download through your signed-in class access.
              </Typography>
            </Box>
            {materials.data?.items.length ? (
              <Stack spacing={1.5}>
                {materials.data.items.map((material) => (
                  <Card key={material.material_id} variant="outlined">
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ justifyContent: "space-between" }}
                        >
                          <Typography variant="h6">{material.title}</Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              material.scope.kind === "class"
                                ? "Whole class"
                                : material.scope.kind
                            }
                          />
                        </Stack>
                        {material.body ? (
                          <Typography sx={{ whiteSpace: "pre-wrap" }}>
                            {material.body}
                          </Typography>
                        ) : null}
                        {material.attachments.length ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ flexWrap: "wrap" }}
                          >
                            {material.attachments.map((file) => (
                              <Button
                                key={file.file_id}
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  void download(
                                    file.file_id,
                                    enrollmentId,
                                  ).catch((error) =>
                                    setNotice(learnerError(error)),
                                  )
                                }
                              >
                                {file.filename}
                              </Button>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <EmptyState
                title="No published materials"
                detail="Your instructor has not added course resources yet."
              />
            )}
            {materials.data ? (
              <AcademyPager page={materials.data} onPage={setMaterialOffset} />
            ) : null}
            <Divider />
            <Box>
              <Typography variant="h6">Assignments</Typography>
              <Typography variant="body2" color="text.secondary">
                Your responses and instructor reviews are private to this
                enrollment.
              </Typography>
            </Box>
            {assignments.data?.items.length ? (
              <Stack spacing={2}>
                {assignments.data.items.map((assignment) =>
                  assignment.target_id ? (
                    <LearnerAssignment
                      key={assignment.assignment_id}
                      programId={programId}
                      enrollmentId={enrollmentId}
                      assignment={assignment}
                      onError={setNotice}
                    />
                  ) : null,
                )}
              </Stack>
            ) : (
              <EmptyState
                title="No assignments"
                detail="New assignments will appear here when your instructor publishes them to you."
              />
            )}
            {assignments.data ? (
              <AcademyPager
                page={assignments.data}
                onPage={setAssignmentOffset}
              />
            ) : null}
          </>
        )}
      </Stack>
    </Paper>
  );
}

function LearnerAssignment({
  programId,
  enrollmentId,
  assignment,
  onError,
}: {
  programId: string;
  enrollmentId: string;
  assignment: Schema["ClassAssignment"];
  onError: (message: string) => void;
}) {
  const targetId = assignment.target_id as string;
  const [submissionOffset, setSubmissionOffset] = useState(0);
  const submissions = useClassSubmissions(targetId, enrollmentId, {
    offset: submissionOffset,
  });
  const save = useSaveClassSubmission(targetId, enrollmentId);
  const submit = useSubmitClassSubmission(targetId, enrollmentId);
  const upload = useUploadClassFile(programId);
  const remove = useRemoveClassFile(programId);
  const latest = submissions.data?.items[0];
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (latest?.state === "draft") setDraft(latest.body ?? "");
  }, [latest?.submission_id, latest?.revision, latest?.state]);

  const writable =
    assignment.target_state === "assigned" ||
    assignment.target_state === "changes_requested";
  async function saveDraft(fileIds: string[] = []) {
    const expected = latest?.state === "draft" ? latest.revision : 0;
    return save.mutateAsync({
      body: draft || null,
      expected_revision: expected,
      idempotency_key: command(),
      file_ids: fileIds,
    });
  }
  async function addFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const current = latest?.state === "draft" ? latest : await saveDraft();
      const stored = await upload.mutateAsync({
        file,
        reserve: {
          subject_kind: "submission",
          subject_id: current.submission_id,
          enrollment_id: enrollmentId,
          expected_subject_revision: current.revision,
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          idempotency_key: command(),
        },
      });
      await save.mutateAsync({
        body: current.body,
        expected_revision: current.revision,
        idempotency_key: command(),
        file_ids: [stored.file_id],
      });
    } catch (error) {
      onError(learnerError(error));
    }
  }

  if (submissions.isError)
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => void submissions.refetch()}>
                Retry
              </Button>
            }
          >
            Assignment work could not be loaded.
          </Alert>
        </CardContent>
      </Card>
    );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ justifyContent: "space-between" }}
          >
            <Box>
              <Typography variant="h6">{assignment.title}</Typography>
              <Typography
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {assignment.instructions}
              </Typography>
            </Box>
            <Chip
              sx={{ alignSelf: "flex-start" }}
              label={statusLabel(assignment.target_state || assignment.state)}
            />
          </Stack>
          {assignment.due_at ? (
            <Typography variant="body2" color="text.secondary">
              {dueLabel(assignment.due_at)}
            </Typography>
          ) : null}
          {assignment.attachments.length ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {assignment.attachments.map((file) => (
                <Button
                  key={file.file_id}
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    void download(file.file_id, enrollmentId).catch((error) =>
                      onError(learnerError(error)),
                    )
                  }
                >
                  {file.filename}
                </Button>
              ))}
            </Stack>
          ) : null}
          {latest?.review ? (
            <Alert
              severity={
                latest.review.disposition === "reviewed" ? "success" : "info"
              }
            >
              <Typography variant="subtitle2">
                {latest.review.disposition === "reviewed"
                  ? "Instructor review"
                  : "Changes requested"}
              </Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {latest.review.body || "Review removed"}
              </Typography>
            </Alert>
          ) : null}
          {latest?.state === "draft" && latest.attachments.length ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {latest.attachments.map((file) => (
                <Chip
                  key={file.file_id}
                  label={file.filename}
                  clickable
                  onClick={() =>
                    void download(file.file_id, enrollmentId).catch((error) =>
                      onError(learnerError(error)),
                    )
                  }
                  onDelete={
                    file.can_remove
                      ? () =>
                          void remove
                            .mutateAsync({
                              fileId: file.file_id,
                              expectedRevision: file.revision,
                            })
                            .catch((error) => onError(learnerError(error)))
                      : undefined
                  }
                />
              ))}
            </Stack>
          ) : null}
          {latest && latest.state !== "draft" ? (
            <Box>
              <Typography variant="subtitle2">Your latest response</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {latest.body || "File submission"}
              </Typography>
              {latest.attachments.map((file) => (
                <Button
                  key={file.file_id}
                  size="small"
                  onClick={() =>
                    void download(file.file_id, enrollmentId).catch((error) =>
                      onError(learnerError(error)),
                    )
                  }
                >
                  {file.filename}
                </Button>
              ))}
            </Box>
          ) : null}
          {writable && submissionOffset === 0 ? (
            <Stack spacing={1.5}>
              <TextField
                label="Your response"
                multiline
                minRows={4}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                helperText="Only authorized class staff can review this response."
              />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ flexWrap: { sm: "wrap" } }}
              >
                <Button
                  variant="outlined"
                  disabled={save.isPending}
                  onClick={() =>
                    void saveDraft().catch((error) =>
                      onError(learnerError(error)),
                    )
                  }
                >
                  Save draft
                </Button>
                <Button component="label" disabled={upload.isPending}>
                  Add file
                  <input
                    hidden
                    type="file"
                    onChange={(event) => void addFile(event)}
                  />
                </Button>
                {latest?.state === "draft" ? (
                  <Button
                    variant="contained"
                    disabled={
                      submit.isPending ||
                      (!latest.body && latest.attachments.length === 0)
                    }
                    onClick={() =>
                      void submit
                        .mutateAsync({
                          submissionId: latest.submission_id,
                          body: {
                            expected_revision: latest.revision,
                            idempotency_key: command(),
                          },
                        })
                        .catch((error) => onError(learnerError(error)))
                    }
                  >
                    Submit
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          ) : null}
          {submissions.data ? (
            <AcademyPager
              page={submissions.data}
              onPage={setSubmissionOffset}
            />
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

function AcademyPager({
  page,
  onPage,
}: {
  page: { total: number; limit: number; offset: number };
  onPage: (offset: number) => void;
}) {
  if (page.total <= page.limit) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
      <Button
        size="small"
        disabled={page.offset <= 0}
        onClick={() => onPage(Math.max(0, page.offset - page.limit))}
      >
        Previous
      </Button>
      <Button
        size="small"
        disabled={page.offset + page.limit >= page.total}
        onClick={() => onPage(page.offset + page.limit)}
      >
        Next
      </Button>
    </Stack>
  );
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <Box
      sx={{
        py: 3,
        px: 2,
        textAlign: "center",
        border: 1,
        borderStyle: "dashed",
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary">{detail}</Typography>
    </Box>
  );
}
